import React, { useState, useEffect, useMemo } from 'react'
import Cards from 'react-credit-cards'
import { Button } from 'core/components/button'
import { requiredMessage, ExpirationDateMask, CellPhoneMask } from 'core/constants'
import { useForm } from 'core/hooks/form'
import { TextInput } from 'core/components/form/text-input'
import { API } from 'service'
import { Field } from 'core/components/form/field'
import { DecimalInput } from 'core/components/form/decimal-input'
import { Select } from 'core/components/form/select'
import { inPath } from 'core/helpers/path'
import { onlyNumbers } from 'core/helpers/format'
import { parseDate, toLocaleTime } from 'core/helpers/date'
import ReCAPTCHA from 'react-google-recaptcha'
import { maskDecimal, maskMoney } from 'core/helpers/mask'
import { AddressForm } from 'components/address'
import { localStore } from 'core/helpers/store'
import { config } from 'config'
import copyToClipboard from 'copy-to-clipboard'

export const CrediPurchaseForm = ({ global: { modal, operator }, route, basename }) => {
  const isRequiredMoreInfo = operator.parameters.gatewayType === 2

  const [qrCodeData, setQrCodeData] = useState(null)
  const [bankslipData, setBankslipData] = useState(null)
  const [sendingBankslip, setSendingBankslip] = useState(false)
  const paymentMethods = useMemo(() => {
    const items = [{ id: 1, name: 'Cartão de Crédito' }]

    if (operator.parameters.allowDebitCard) {
      items.push({ id: 2, name: 'Cartão de Débito' })
    }

    if (operator.parameters.allowPix) {
      items.push({ id: 3, name: 'PIX' })
    }

    if (operator.parameters.allowBankslip) {
      items.push({ id: 4, name: 'Boleto' })
    }

    return items
  }, [operator])

  const form = useForm(
    {
      displayName: ent => ent.name,
      initialEntity: {
        customerCpfCnpj: operator.user.cpfCnpj,
        value: 0,
        valueId: 0,
        paymentMethodId: 1,
        paymentMethodName: 'Cartão de Crédito',
        cpfCnpj: '',
        email: '',
        phone: '',
        postalCode: '',
        streetName: '',
        streetNumber: '',
        district: '',
        city: '',
        state: '',
        card: {
          number: '',
          name: '',
          expiration: '',
          securityCode: '',
        },
        focused: 'number',
        token: '',
      },
      validate: values => {
        const errors = {}

        if (!values.value) {
          errors.value = requiredMessage
        }

        if (values.paymentMethodName.includes('Cartão')) {
          if (!values.card.number) {
            errors.card = { ...errors.card, number: requiredMessage }
          }

          if (!values.card.name) {
            errors.card = { ...errors.card, name: requiredMessage }
          }

          if (!values.card.expiration) {
            errors.card = { ...errors.card, expiration: requiredMessage }
          } else if (!parseDate(values.card.expiration, 'MM/yy')) {
            errors.card = { ...errors.card, expiration: 'Data de validade inválida.' }
          }

          if (!values.card.securityCode) {
            errors.card = { ...errors.card, securityCode: requiredMessage }
          }

          if (isRequiredMoreInfo) {
            if (!values.cpfCnpj) {
              errors.cpfCnpj = requiredMessage
            }

            if (!values.email) {
              errors.email = requiredMessage
            }

            if (!values.phone) {
              errors.phone = requiredMessage
            }
          }
        }

        if (isRequiredAddress) {
          if (!values.postalCode) {
            errors.postalCode = ' '
          }

          if (!values.streetName) {
            errors.streetName = requiredMessage
          }

          if (!values.streetNumber) {
            errors.streetNumber = requiredMessage
          }

          if (!values.district) {
            errors.district = requiredMessage
          }

          if (!values.city) {
            errors.city = requiredMessage
          }

          if (!values.state) {
            errors.state = requiredMessage
          }
        }

        return errors
      },
    },
    route,
  )

  const { entity, errors, touched, handleChange, submitting } = form

  const isRequiredAddress =
  operator.parameters.gatewayType === 5 || entity.paymentMethodName.includes('Boleto')

  const cardFocus = type => {
    if (type === 'blur') {
      form.setValues({ focused: null })
    }
  }

  const [values, setValues] = useState([])

  const minParkingFeeValue = useMemo(() => {
    const parkingFees = operator.parameters.parkingFees || []
    if (parkingFees.length === 0) return operator.parameters.pixTransactionMinValue || 0
    const today = new Date().getDay()
    const todayFees = parkingFees.filter(fee => {
      if (fee.dayOfWeek === null || fee.dayOfWeek === undefined) return true
      return fee.dayOfWeek === today
    })
    const feesToUse = todayFees.length > 0 ? todayFees : parkingFees
    if (feesToUse.length === 0) return operator.parameters.pixTransactionMinValue || 0
    return Math.min(...feesToUse.map(fee => fee.value))
  }, [operator.parameters.parkingFees, operator.parameters.pixTransactionMinValue])

  useEffect(() => {
    const { parameters } = localStore.get(config.OPERATOR_KEY)
    const listValues = parameters.values && parameters.values.length ? parameters.values : []

    API
      .get('customer.key', operator.user.id)
      .then((data) => {
        form.setValues(prev => ({
          ...prev,
          city: data.city,
          complement: data.complement,
          district: data.district,
          postalCode: data.postalCode,
          state: data.state,
          streetName: data.streetName,
          streetNumber: data.streetNumber,
        }))
      })
      .catch(err => modal.alert(err.message))

    setValues(listValues.map(item => ({ ...item, mask: maskMoney(item.value) })))
		// eslint-disable-next-line
  }, [])

  return (
    <div className="kt-portlet">
      <div className="kt-portlet__head">
        <div className="kt-portlet__head-label">
          <span className="kt-portlet__head-icon">
            <i className="kt-font-brand fas fa-dollar-sign" />
          </span>
          <h3 className="kt-portlet__head-title">Comprar Créditos</h3>
        </div>
      </div>
      <div className="kt-portlet__body">
        <form
          onSubmit={ev => {
            ev.preventDefault()
            form.handleSubmit(data => {
              creditPurchaseSubmit(data, operator.parameters, operator.user.id)
                .then(resp => {
                  const isBankSlip = data.paymentMethodName.includes('Boleto')
                  const isPix = data.paymentMethodName.includes('PIX')

                  if (isBankSlip) {
                    setBankslipData(resp)
                  } else if (isPix) {
                    setQrCodeData(resp)
                  } else if (resp.debitPaymentUrl) {
                    window.open(resp.debitPaymentUrl)
                  } else {
                    modal.alert(`Você adquiriu ${maskMoney(resp.value)} de créditos.`, () =>
                      route.history.push(`${basename}`),
                    )
                  }
                })
                .catch(err => {
                  if (window.grecaptcha && data.paymentMethodName.includes('Cartão')) {
                    window.grecaptcha.reset()
                  }

                  modal.alert(err.message)
                })
                .finally(() => form.setSubmitting(false))
            })
          }}
        >
          <Field className="field-xl" label="Modo de Pagamento">
            <Select
              getDisplay={s => s.name}
              getId={s => s.id}
              items={paymentMethods}
              meta={{
                error: errors.paymentMethodId,
                touched: touched.paymentMethodId,
              }}
              onChange={s =>
                handleChange({
                  path: 'paymentMethodId',
                  values: {
                    paymentMethodId: s ? s.id : 0,
                    paymentMethodName: s.name,
                  },
                })
              }
              placeholder="Selecione um modo de pagamento..."
              selected={entity.paymentMethodId}
            />
          </Field>

          <Field className="field-xl" label="Valor">
            {values.length ? (
              <Select
                getDisplay={s => s.mask}
                getId={s => s.id}
                items={values}
                meta={{
                  error: errors.value,
                  touched: touched.value,
                }}
                onChange={s =>
                  handleChange({
                    path: 'value',
                    values: { valueId: s.id, value: s.value },
                    type: 'key',
                  })
                }
                placeholder="Selecione o valor pagamento..."
                selected={entity.valueId}
              />
            ) : (
              <DecimalInput
                acceptEnter
                customClassName="form-control-xl"
                icon="fas fa-dollar-sign"
                meta={{
                  error: errors.value,
                  touched: touched.value,
                }}
                onChange={(value, type) => {
                  handleChange({
                    path: 'value',
                    type,
                    values: { value },
                  })
                }}
                value={entity.value}
                placeholder={
                  !operator.user.ignoreMaxValues
                    ? `Entre ${maskDecimal(
                        entity.paymentMethodName.includes('PIX')
                          ? minParkingFeeValue
                          : entity.paymentMethodName.includes('Boleto')
                          ? operator.parameters.bankslipTransactionMinValue
                          : operator.parameters.cardTransactionMinValue,
                      )} e ${maskDecimal(
                        entity.paymentMethodName.includes('PIX')
                          ? operator.parameters.pixTransactionMaxValue
                          : entity.paymentMethodName.includes('Boleto')
                          ? operator.parameters.bankslipTransactionMaxValue
                          : operator.parameters.cardTransactionMaxValue,
                      )}`
                    : ''
                }
              />
            )}
          </Field>

          {entity.paymentMethodName.includes('Cartão') && (
            <>
              {isRequiredMoreInfo && (
                <div className="row">
                  <Field className="field-xl col-lg" label="CPF/CNPJ do Titular">
                    <TextInput
                      name="cpfCnpj"
                      customClassName="form-control-xl"
                      meta={{ error: errors.cpfCnpj, touched: touched.cpfCnpj }}
                      onChange={(cpfCnpj, type) => {
                        handleChange({
                          path: 'cpfCnpj',
                          type,
                          values: prev => ({ ...prev, cpfCnpj }),
                        })
                      }}
                      value={entity.cpfCnpj}
                    />
                  </Field>

                  <Field className="field-xl col-lg" label="Email do Titular">
                    <TextInput
                      name="email"
                      autoComplete="email"
                      customClassName="form-control-xl"
                      meta={{ error: errors.email, touched: touched.email }}
                      onChange={(email, type) => {
                        handleChange({
                          path: 'email',
                          type,
                          values: prev => ({ ...prev, email }),
                        })
                      }}
                      value={entity.email}
                    />
                  </Field>

                  <Field className="field-xl col-lg" label="Telefone do Titular">
                    <TextInput
                      name="phone"
                      customClassName="form-control-xl"
                      mask={CellPhoneMask}
                      meta={{ error: errors.phone, touched: touched.phone }}
                      onChange={(phone, type) => {
                        handleChange({
                          path: 'phone',
                          type,
                          values: prev => ({ ...prev, phone }),
                        })
                      }}
                      value={entity.phone}
                    />
                  </Field>
                </div>
              )}

              <div className="row">
                <div className="col-lg">
                  <Field className="field-xl" label="Número do Cartão">
                    <TextInput
                      name="cardnumber"
                      autoComplete="cc-number"
                      customClassName="form-control-xl"
                      maxLength={19}
                      meta={{
                        error: inPath(errors, 'card.number'),
                        touched: inPath(touched, 'card.number'),
                      }}
                      onChange={(val, type) => {
                        cardFocus(type)
                        handleChange({
                          path: 'card.number',
                          type,
                          values: prev => ({
                            ...prev,
                            card: {
                              ...prev.card,
                              number: onlyNumbers(val),
                            },
                          }),
                        })
                      }}
                      onFocus={() =>
                        form.setValues({
                          focused: 'number',
                        })
                      }
                      value={entity.card.number}
                    />
                  </Field>

                  <Field className="field-xl" label="Nome do Titular">
                    <TextInput
                      name="ccname"
                      autoComplete="cc-name"
                      customClassName="form-control-xl"
                      meta={{
                        error: inPath(errors, 'card.name'),
                        touched: inPath(touched, 'card.name'),
                      }}
                      onChange={(name, type) => {
                        cardFocus(type)
                        handleChange({
                          path: 'card.name',
                          type,
                          values: prev => ({
                            ...prev,
                            card: { ...prev.card, name },
                          }),
                        })
                      }}
                      onFocus={() =>
                        form.setValues({
                          focused: 'name',
                        })
                      }
                      value={entity.name}
                    />
                  </Field>

                  <div className="row">
                    <Field className="field-xl col-lg" label="Validade do Cartão">
                      <TextInput
                        name="cc-exp"
                        autoComplete="cc-exp"
                        customClassName="form-control-xl"
                        mask={ExpirationDateMask}
                        meta={{
                          error: inPath(errors, 'card.expiration'),
                          touched: inPath(touched, 'card.expiration'),
                        }}
                        onChange={(expiration, type) => {
                          cardFocus(type)
                          handleChange({
                            path: 'card.expiration',
                            type,
                            values: prev => ({
                              ...prev,
                              card: { ...prev.card, expiration },
                            }),
                          })
                        }}
                        onFocus={() =>
                          form.setValues({
                            focused: 'expiry',
                          })
                        }
                        value={entity.card.expiration}
                      />
                    </Field>

                    <Field className="field-xl col-lg" label="Código de Segurança">
                      <TextInput
                        name="cvc"
                        autoComplete="cc-csc"
                        customClassName="form-control-xl"
                        maxLength={4}
                        meta={{
                          error: inPath(errors, 'card.securityCode'),
                          touched: inPath(touched, 'card.securityCode'),
                        }}
                        onChange={(securityCode, type) => {
                          cardFocus(type)
                          handleChange({
                            path: 'card.securityCode',
                            type,
                            values: prev => ({
                              ...prev,
                              card: { ...prev.card, securityCode },
                            }),
                          })
                        }}
                        onFocus={() =>
                          form.setValues({
                            focused: 'cvc',
                          })
                        }
                        value={entity.securityCode}
                      />
                    </Field>
                  </div>
                </div>
                <div className="col-lg d-flex align-items-center justify-content-center">
                  <Cards
                    cvc={entity.card.securityCode}
                    expiry={entity.card.expiration}
                    focused={entity.focused || undefined}
                    locale={{
                      valid: 'valido até',
                    }}
                    name={entity.card.name}
                    number={entity.card.number}
                    placeholders={{
                      name: 'SEU NOME AQUI',
                    }}
                  />
                </div>
              </div>

              <ReCAPTCHA
                onChange={token => {
                  form.setValues({ token })
                }}
                sitekey="6LdRERoUAAAAAHvxprmXTb24rCkzAVEXD1XU6NkC"
              />
            </>
          )}

          {isRequiredAddress && (
            <>
              <div className="kt-separator kt-separator--border-dashed kt-separator--space-lg" />
              <label className="title">Endereço de Cobrança</label>
              <AddressForm form={form} />
            </>
          )}

          {qrCodeData && (
            <>
              <div className="row">
                <div className="col">
                  <Field label="Código Pix">
                    <h3 className="h4 pix">{qrCodeData.qrCode}</h3>
                  </Field>

                  <div className="pix-button">
                    <Button
                      type="button"
                      customClassName="btn-primary btn-lg"
                      title="Copiar Código Pix"
                      icon={qrCodeData.copied ? 'fas fa-check' : null}
                      onClick={() => {
                        setQrCodeData(prev => ({ ...prev, copied: true }))
                        copyToClipboard(qrCodeData.qrCode)
                      }}
                    />
                  </div>

                  <span className="h5">Expira às {toLocaleTime(qrCodeData.expiresAt, true)}</span>
                </div>
              </div>
            </>
          )}

          {bankslipData && (
            <>
              <div className="row">
                <div className="col">
                  <Field label="Código de Barras">
                    <input
                      readOnly
                      type="text"
                      className="form-control form-control-xl"
                      value={bankslipData.barCode}
                      onFocus={ev => ev.target.select()}
                    />
                  </Field>
                </div>
              </div>

              <div className="col-lg kt-align-right">
                <Button
                  type="button"
                  loading={sendingBankslip}
                  disabled={sendingBankslip}
                  customClassName="btn-secondary"
                  icon="fas fa-envelope"
                  title="Enviar no Email"
                  onClick={() => {
                    setSendingBankslip(true)

                    API.post('customer.bankslip', { url: bankslipData.url })
                      .then(() =>
                        modal.alert('Boleto enviado com sucesso.', () =>
                          route.history.push(`${basename}`),
                        ),
                      )
                      .catch(err => modal.alert(err.message))
                      .finally(() => setSendingBankslip(false))
                  }}
                />
              </div>
            </>
          )}

          {!qrCodeData && !bankslipData && (
            <div className="col-lg kt-align-right">
              <Button
                customClassName="btn-secondary"
                disabled={submitting}
                icon="fas fa-arrow-right"
                loading={submitting}
                title="Confirmar"
              />
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

async function creditPurchaseSubmit(data, parameters, operatorId) {
  const isCreditCard = data.paymentMethodName.includes('Cartão')
  const isDebit = data.paymentMethodName.includes('Débito')
  const isBankSlip = data.paymentMethodName.includes('Boleto')
  const isPix = data.paymentMethodName.includes('PIX')

  let url = 'credit.money'

  if (isCreditCard) url = 'credit.card'
  else if (isBankSlip) url = 'credit.bankslip'
  else if (isPix) url = 'credit.pix'

  const card =
    isCreditCard || isDebit
      ? { ...data.card, expiration: parseDate(data.card.expiration, 'MM/yy') }
      : data.card

  let threedsAuthId = ''

  if (isCreditCard && parameters.gatewayType === 5) {
    const expMonth = String(card.expiration.getMonth() + 1).padStart(2, '0')
    const expYear = String(card.expiration.getFullYear())
    const cardData = window.PagSeguro.encryptCard({
      publicKey: parameters.pagSeguroPublicKey,
      holder: card.name,
      number: card.number?.replace(/\D/g, ''),
      expMonth,
      expYear,
      securityCode: card.securityCode,
    })

    card.encrypted = cardData.encryptedCard

    if (isDebit) {
      const sessionData = await API.post('threeds.session', { type: 'debit' })

      window.PagSeguro.setUp({ session: sessionData.session, env: sessionData.env })

      const customerData = await API.get(`customer.key/${operatorId}`)
      const threedsData = await window.PagSeguro.authenticate3DS({
        data: {
          customer: {
            name: customerData.name,
            email: customerData.email,
            phones: [
              {
                country: '55',
                area: customerData.phone.substring(0, 2),
                number: customerData.phone.substring(2),
                type: 'MOBILE',
              },
            ],
          },
          paymentMethod: {
            type: 'DEBIT_CARD',
            installments: 1,
            card: { number: card.number, expMonth, expYear, holder: { name: card.name } },
          },
          amount: { value: Number(data.value) * 100, currency: 'BRL' },
          billingAddress: {
            street: data.streetName,
            number: data.streetNumber,
            complement: data.complement || 'N/A',
            regionCode: data.state,
            city: data.city,
            postalCode: data.postalCode?.replace(/\D/g, ''),
            country: 'BRA',
          },
          dataOnly: false,
        },
      })

      if (threedsData.status === 'AUTH_FLOW_COMPLETED') {
        threedsAuthId = threedsData.id
      } else {
        console.error(threedsData)
        throw new Error('Cartão não suportado para esta forma de pagamento.')
      }
    }
  }

  return API.post(url, { ...data, threedsAuthId, paymentType: isDebit ? 4 : null, card })
}
